import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/utils/style/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "rounded-full border border-supa-border bg-white text-foreground hover:bg-accent",
        destructive:
          "rounded-full bg-destructive text-white hover:bg-destructive/90",
        outline:
          "rounded-full border border-supa-border-strong bg-white text-foreground hover:bg-accent",
        secondary:
          "rounded-full border border-supa-border bg-accent text-foreground hover:bg-supa-border",
        filled:
          "rounded-full bg-primary text-primary-foreground hover:bg-primary/90",
        ghost: "rounded-md bg-transparent text-foreground hover:bg-accent",
        link: "text-supa-green-link underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-8 py-2",
        sm: "h-7 px-4 text-xs",
        lg: "h-11 px-10",
        icon: "size-9 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
